import { supabase } from "@/lib/supabase";
import { addHours } from "date-fns";
import { CalendarEvent, DbCalendarEvent } from "./types";
import { updatePackageHours, returnPackageHours } from "./package-operations";
import { mapDbEventToCalendarEvent, mapCalendarEventToDb } from "./event-mappers";
import { toast as sonnerToast } from "sonner";

export const addCalendarEvent = async (
  eventData: Omit<CalendarEvent, "id">,
  isClientView: boolean,
  setCalendarEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>,
  setError: (error: Error | null) => void,
  toast: any
): Promise<CalendarEvent | void> => {
  try {
    const isClientRequest = isClientView && eventData.clientId;
    const eventStatus = isClientRequest ? 'pending' as const : 'approved' as const;
    
    if (!eventData.clientId) {
      throw new Error("ClientId è obbligatorio per creare un evento del calendario");
    }
    
    const dbEventData = mapCalendarEventToDb({
      ...eventData,
      status: eventStatus
    });
    
    console.log("Inserimento nuovo evento:", dbEventData);
    
    const { data, error } = await supabase
      .from("calendar_events")
      .insert(dbEventData)
      .select("*")
      .single();
      
    if (error) {
      console.error("Errore nell'inserimento dell'evento:", error);
      setError(error);
      throw error;
    }
    
    console.log("Evento inserito con successo:", data);
    
    const newEvent = mapDbEventToCalendarEvent(data as DbCalendarEvent, new Map());
    
    setCalendarEvents(prev => [...prev, newEvent]);
    
    if (!isClientRequest && eventData.clientId) {
      await updatePackageHours(eventData.clientId, data.id, 1);
    }
    
    if (isClientRequest) {
      toast({
        title: "Richiesta inviata",
        description: "La tua richiesta di sessione è stata inviata e sarà esaminata dall'amministratore",
      });
    } else {
      toast({
        title: "Sessione aggiunta",
        description: "La sessione è stata aggiunta al calendario con successo",
      });
    }
    
    return newEvent;
  } catch (error) {
    console.error("Errore nell'aggiunta dell'evento:", error);
    setError(error instanceof Error ? error : new Error('Errore sconosciuto'));
    toast({
      title: "Errore",
      description: "Impossibile aggiungere la sessione al calendario",
      variant: "destructive"
    });
    throw error;
  }
};

export const deleteCalendarEvent = async (
  event: CalendarEvent,
  isClientView: boolean,
  setCalendarEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>,
  setError: (error: Error | null) => void,
  toast: any
) => {
  if (isClientView) {
    toast({
      title: "Accesso limitato",
      description: "Non hai i permessi per eliminare eventi dal calendario",
      variant: "destructive"
    });
    return;
  }
  
  try {
    if (event.type === "course" && event.id.includes('-')) {
      toast({
        title: "Informazione",
        description: "Gli eventi dei corsi non possono essere eliminati singolarmente. Modifica le impostazioni del corso.",
        variant: "warning"
      });
      return;
    }
    
    console.log("Tentativo di eliminazione dell'evento con ID:", event.id);
    
    try {
      const { error: notificationsError } = await supabase
        .from("notifications")
        .delete()
        .eq("session_id", event.id);
        
      if (notificationsError) {
        console.error("Errore nell'eliminazione delle notifiche associate:", notificationsError);
        // Non blocchiamo l'operazione, proviamo comunque a eliminare l'evento
      }
    } catch (notificationErr) {
      console.warn("Errore nella cancellazione delle notifiche, ma continuiamo:", notificationErr);
    }
    
    console.log("Esecuzione query DELETE per evento:", event.id);
    
    const { error, data, count, status } = await supabase
      .from("calendar_events")
      .delete()
      .eq("id", event.id)
      .select();
      
    console.log("Risultato DELETE:", { error, data, count, status });
      
    if (error) {
      console.error("Errore nell'eliminazione dell'evento:", error);
      
      if (error.message.includes("violates row-level security")) {
        throw new Error("Non hai i permessi per eliminare questo evento. Verifica di essere un amministratore.");
      }
      
      setError(error);
      throw error;
    }
    
    console.log("Evento eliminato con successo dal database:", event.id);
    
    setCalendarEvents(prev => prev.filter(e => e.id !== event.id));
    
    sonnerToast.success("Sessione eliminata con successo");
    
    if (event.type === "session" && event.clientId) {
      try {
        await returnPackageHours(event.clientId, event.id, 1);
      } catch (packageErr) {
        console.error("Errore nella restituzione delle ore:", packageErr);
        toast({
          title: "Sessione eliminata",
          description: "L'evento è stato eliminato ma si è verificato un errore nella restituzione delle ore",
          variant: "warning"
        });
      }
    }
  } catch (error) {
    console.error("Errore nell'eliminazione dell'evento:", error);
    setError(error instanceof Error ? error : new Error('Errore sconosciuto'));
    toast({
      title: "Errore",
      description: error instanceof Error ? error.message : "Impossibile eliminare l'evento dal calendario",
      variant: "destructive"
    });
  }
};

export const changeSessionStatus = async (
  eventId: string,
  newStatus: 'approved' | 'rejected',
  setError: (error: Error | null) => void,
  toast: any
) => {
  try {
    const { data: eventData, error: fetchError } = await supabase
      .from("calendar_events")
      .select("*")
      .eq("id", eventId)
      .single();
      
    if (fetchError) {
      console.error("Errore nel recupero dell'evento:", fetchError);
      setError(fetchError);
      throw fetchError;
    }
    
    const { error: updateError } = await supabase
      .from("calendar_events")
      .update({ status: newStatus })
      .eq("id", eventId);
      
    if (updateError) {
      console.error("Errore nell'aggiornamento dello stato dell'evento:", updateError);
      setError(updateError);
      throw updateError;
    }
    
    if (newStatus === 'approved' && eventData.client_id) {
      await updatePackageHours(eventData.client_id, eventData.id, 1);
    }
    
    toast({
      title: newStatus === 'approved' ? "Sessione approvata" : "Sessione rifiutata",
      description: newStatus === 'approved' 
        ? "La richiesta di sessione è stata approvata con successo"
        : "La richiesta di sessione è stata rifiutata",
    });
    
    return true;
  } catch (error) {
    console.error("Errore nel cambiamento dello stato della sessione:", error);
    setError(error instanceof Error ? error : new Error('Errore sconosciuto'));
    toast({
      title: "Errore",
      description: "Impossibile aggiornare lo stato della sessione",
      variant: "destructive"
    });
    return false;
  }
};
